import React from 'react';
import { get } from 'lodash';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { updateVehicle } from 'client/actions/vehicle';
import { TypePageDecorator } from 'site-modules/upper-funnel/decorators/type-page-decorator';
import { defineSeoContent } from 'site-modules/upper-funnel/utils/seo-content';
import { buildSubTypeRankingsPath, TypeRankingsModel } from 'client/data/models/type-rankings';
import { SuggestedVehicleModel } from 'client/data/models/suggested-vehicle';
import { setCriticalCss } from 'client/actions/critical-css';
import { hasMostSoldTypeContent, hasSubsegments } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { getSubtypeContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';
import { CmsModel } from 'client/data/models/cms';
import { HomeModulesModel } from 'client/data/models/home-modules';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { SEGMENT_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'site-modules/shared/components/native-ad/utils/utils';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { isLeaseDealsLinkEligible } from 'site-modules/upper-funnel/utils/is-lease-deals-link-eligible';
import { TypeLeaseDealsModel, TypeLeaseDealsPaths } from 'client/data/models/type-lease-deals';
import { buildLeaseDealsHiddenGemsCriticalCss } from 'site-modules/upper-funnel/utils/lease-deals-hidden-gems-critical-css';
import { SEOModel } from 'client/data/models/seo';
import { getIsSeot4733 } from 'client/utils/seot-4733';

import { SubtypeRenderer } from './subtype-renderer';

export const pageDefinitionConfig = ({ type, subtype }) => ({
  name: 'new_subtype_index',
  category: 'new_type_index',
  preload: store => {
    const { mobile, featureFlags } = store.getState();
    const preloader = new ModelPreloader(store);

    const articlePath = getSubtypeContentPath(type, subtype);
    const rankingsPath = buildSubTypeRankingsPath(`${type},${subtype}`, subtype);
    const isSeot4733 = getIsSeot4733(get(featureFlags, 'seot-4733-level-2-subtype-pages'), type, subtype);

    if (isSeot4733) {
      preloader.resolve('bestRankings', SEOModel);
    } else {
      preloader.resolve(rankingsPath, TypeRankingsModel);
    }
    preloader.resolve(`content["${articlePath}"]`, CmsModel);
    preloader.resolve('vehicleShowcaseContent', HomeModulesModel);

    if (isLeaseDealsLinkEligible(type)) {
      preloader.resolve(TypeLeaseDealsPaths.getTypeLeaseDealsBodyEngineTypePath(type), TypeLeaseDealsModel);
    }

    const vehicle = { type: { niceName: type } };
    store.dispatch(updateVehicle(vehicle));
    setAdsListToPreload(preloader, store.getState(), [SEGMENT_LISTING_AD.AD_NAME], vehicle);

    return preloader.load().then(modelState => {
      const vehicleEditorialRankings = isSeot4733
        ? get(modelState.get('bestRankings', SEOModel), 'rankings', [])
        : modelState.get(rankingsPath, TypeRankingsModel);
      const siteServedAds = modelState.get(
        getCreativeConfigAdPathByVehicle(mobile, vehicle),
        NativeAdsCreativeConfigModel
      );
      const leaseDeals = modelState.get(
        TypeLeaseDealsPaths.getTypeLeaseDealsBodyEngineTypePath(type),
        TypeLeaseDealsModel
      );
      const isAds11436Enabled =
        ExperimentUtil.getForcedOrAssignedRecipeName({
          state: store.getState(),
          campaignName: 'ads-11436',
          defaultVal: 'ctrl',
        }) === 'chal';

      const firstVehicle = vehicleEditorialRankings?.[0]?.segmentRatings?.[0]?.modelYear;
      if (firstVehicle) {
        preloader.set('forcedVehicleParams', SuggestedVehicleModel, {
          make: firstVehicle.makeSlug,
          model: firstVehicle.modelSlug,
          year: firstVehicle.year,
        });
      }

      store.dispatch(
        setCriticalCss({
          type,
          criticalCssHeight: 2000,
          hasAnchorNav: hasSubsegments(vehicleEditorialRankings) || hasMostSoldTypeContent(type, subtype),
          ...siteServedAdsCriticalCss(siteServedAds),
          topRatedPromo: !!modelState.get('top-rated-promo', FeatureFlagModel),
          isAds11436Enabled,
          ...buildLeaseDealsHiddenGemsCriticalCss({ vehicleEditorialRankings, leaseDeals }),
        })
      );
      return preloader.load();
    });
  },
  component: props => <SubtypeRenderer {...props} type={type} subtype={subtype} />,
  decorator: TypePageDecorator,
  seo: (state, props) => defineSeoContent(state, props, type, subtype),
  options: {
    hasVehicleContext: true,
  },
});

const pages = new Map();

export default params => {
  const { type, subtype } = params;
  const key = buildSubTypeRankingsPath(`${type},${subtype}`, subtype);
  let page = pages.get(key);
  if (!page) {
    page = pageDefinition(pageDefinitionConfig(params));
    pages.set(key, page);
  }
  return page;
};
